"use client";
import { Button } from "@/components/ui/button";
import { IBlocks, IUserData } from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import AdminPopover from "../../content/AdminPopover";
import { SettingTabs } from "../../content/SettingTabs";
import DividerCustomization, { BlockOptions } from "./tabs/divider";
import { Separator } from "@/components/ui/separator";
import BlockMetadata from "./tabs/metadata";

interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    newBlock?: string | null;
}
export default function BlockDivider({
    admin,
    user,
    block,
    update,
    newBlock,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Divider",
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <DividerCustomization
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
            titleOptions: (
                <BlockOptions
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    return (
        <AdminPopover
            admin={admin}
            className={`pb-5 ${block.show ? "" : "opacity-30"}`}
            title={"Divider"}
            onClick={handleExpand}
            isOpen={isExpanded}
            onClose={handleClose}
        >
            <div
                className={`mb-2 mt-1 w-full border-b-2 ${admin && "pt-5"}`}
                id="divider"
                style={{
                    borderColor: block.colour.toggle
                        ? block.colour.background
                        : "white",
                }}
            />
        </AdminPopover>
    );
}
